import React from 'react';
import { LuCheck, LuClipboard } from 'react-icons/lu';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { logger } from 'utils';
import { useClipboard } from 'utils/hooks';

const DOT_COLOR = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
} as const;

interface SourceGuidProps {
    guid: string;
    label?: string;
    dotColor?: keyof typeof DOT_COLOR;
    onClick?: () => void;
}

export const SourceGuid = ({ guid, label, dotColor = 'green', onClick }: SourceGuidProps) => {
    const baseClassName =
        'bg-muted text-foreground inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium';

    const inner = (
        <>
            <span className={cn('inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full', DOT_COLOR[dotColor])} />
            {label}
            <span className="text-muted-foreground font-mono">{guid.slice(0, 8)}…</span>
            <CopyButton value={guid} />
        </>
    );

    if (onClick) {
        return (
            <button
                onClick={onClick}
                className={cn(baseClassName, 'hover:bg-muted/80 cursor-pointer border-0 transition-colors')}
            >
                {inner}
            </button>
        );
    }

    return <span className={baseClassName}>{inner}</span>;
};

const CopyButton = ({ value }: { value: string }) => {
    const { isCopied, copy } = useClipboard({
        timeoutMs: 1000,
        onError: (error) => logger.error('Failed to copy GUID:', error),
    });

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        void copy(value, { event: e });
    };

    return (
        <TooltipProvider delayDuration={0}>
            <Tooltip key={isCopied ? 'copied' : 'normal'} open={isCopied ? true : undefined}>
                <TooltipTrigger asChild>
                    <span onClick={handleClick} className="hover:text-foreground text-muted-foreground cursor-pointer">
                        {isCopied ? <LuCheck className="h-3 w-3" /> : <LuClipboard className="h-3 w-3" />}
                    </span>
                </TooltipTrigger>
                <TooltipContent>{isCopied ? 'Copied!' : 'Copy GUID'}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
